/**
 * =============================================================================
 * useDynamicFavicon.ts - Favicon dinamica per tenant
 * =============================================================================
 *
 * Usa favicon_url del tenant risolto; su assenza/errore torna al fallback.
 * - slug "lni"     → /favicon-lni.png   (ancora blu LNI)
 * - slug "beta"    → /favicon-beta.png  (lettera B verde Beta)
 * - null / altro   → /favicon-default.png (generico formazione)
 *
 * @version 1.1.0
 * =============================================================================
 */

import { useEffect } from "react";

/** Mappa slug → path favicon (nella cartella public) */
const FAVICON_MAP: Record<string, string> = {
  lni: "/favicon-lni.png",
  beta: "/favicon-beta.png",
};

const DEFAULT_FAVICON = "/favicon-default.png";

/**
 * Hook che imposta dinamicamente la favicon del documento
 * in base allo slug del tenant passato come argomento.
 *
 * @param slug - Slug del tenant corrente (es. "lni", "beta") o null
 */
export function useDynamicFavicon(slug: string | null, faviconUrl: string | null = null, isLni = slug === "lni"): void {
  useEffect(() => {
    const fallback = isLni ? FAVICON_MAP.lni : (slug ? FAVICON_MAP[slug] : null) || DEFAULT_FAVICON;
    const configured = faviconUrl?.trim();
    let active = true;

    /** Update every existing icon so the browser cannot pick a stale static one. */
    const applyIcon = (href: string) => {
      let links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
      if (!links.length) {
        const link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
        links = [link];
      }
      const appleLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
      if (appleLink) links.push(appleLink);
      links.forEach((link) => {
        link.href = href;
        // Let the browser infer PNG/SVG/ICO instead of retaining the static MIME/size.
        link.removeAttribute("type");
        link.removeAttribute("sizes");
      });
    };

    applyIcon(configured || fallback);
    const image = configured ? new Image() : null;
    if (image && configured) {
      // A late failure from the previous tenant must not overwrite the current icon.
      image.onerror = () => { if (active) applyIcon(fallback); };
      image.src = configured;
    }
    return () => {
      active = false;
      if (image) image.onerror = null;
    };
  }, [slug, faviconUrl, isLni]);
}
